import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Stack,
  Tabs,
  Tab,
  Alert,
  Card,
  Chip,
  Divider,
  CircularProgress,
  Collapse,
  Stepper,
  Step,
  StepLabel,
  Tooltip,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../../api/client";
import { PAID_ADS_PROVIDER_CARDS } from "../../../constants/paidAdsCampaignCatalog";
import PaidAdsProviderSetup from "./PaidAdsProviderSetup";

const META_ESSENTIAL = ["campaignName", "dailyBudget", "pageId", "link"];
const META_CREATIVE = ["message", "headline", "description", "imageUrl"];
const GOOGLE_ESSENTIAL = ["campaignName", "finalUrl", "headline", "description"];
const GOOGLE_BIDDING = ["dailyBudgetMicros", "cpcBidMicros"];

const defaultMetaConfig = () => ({
  campaignName: "",
  dailyBudget: 500,
  pageId: "",
  link: "https://xpro.me",
  message: "",
  headline: "",
  description: "",
  imageUrl: "",
});

const defaultGoogleConfig = () => ({
  campaignName: "",
  finalUrl: "https://xpro.me",
  headline: "XPro",
  description: "Campanha criada pelo XPro",
  dailyBudgetMicros: 5000000,
  cpcBidMicros: 500000,
});

function PaidAdsProviderTypeCard({ card, selected, onClick, title, description, selectedLabel }) {
  const Icon = card.icon;
  return (
    <Card
      onClick={onClick}
      role="button"
      aria-pressed={selected}
      sx={(theme) => ({
        p: 2.75,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        cursor: "pointer",
        transition: "box-shadow .2s ease, transform .05s ease",
        "&:hover": { boxShadow: 4 },
        boxShadow: selected ? `inset 0 0 0 2px ${theme.palette.primary.main}` : "none",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minHeight: 140,
        width: "100%",
      })}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box sx={{ color: card.color ?? "inherit", display: "flex" }}>{Icon({})}</Box>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
      <Box sx={{ mt: "auto", minHeight: 32, display: "flex", alignItems: "flex-end" }}>
        {selected ? <Chip size="small" label={selectedLabel} color="primary" /> : null}
      </Box>
    </Card>
  );
}

async function pollJob(jobId) {
  let last = null;
  for (let i = 0; i < 60; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const res = await fetchData(`/ads/jobs/${jobId}`, "GET");
    const job = res?.data;
    const st = job?.status;
    last = job;
    if (st === "completed" || st === "failed") break;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 2000));
  }
  return last;
}

export default function PaidAdsCampaignForm({
  campaign,
  connections,
  onClose,
  onAfterSave,
  onAfterPublish,
  onCampaignCreated,
  onConfigureProvider,
  providerSetupProps,
}) {
  const { t } = useTranslation("settings");
  const [tab, setTab] = useState(0);
  const [name, setName] = useState("");
  const [provider, setProvider] = useState(null);
  const [config, setConfig] = useState({});
  const [campaignId, setCampaignId] = useState(null);
  const [status, setStatus] = useState("draft");
  const [externalId, setExternalId] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPayload, setAnalyticsPayload] = useState(null);
  const [optionalMetaCreativeOpen, setOptionalMetaCreativeOpen] = useState(false);
  const [optionalGoogleBiddingOpen, setOptionalGoogleBiddingOpen] = useState(false);
  const [providerDraft, setProviderDraft] = useState(null);

  const isNew = !campaign?.id && !campaignId;
  const metaOk = Boolean(connections?.meta?.connected) && Boolean(connections?.meta?.adAccountId);
  const googleOk = Boolean(connections?.google?.connected) && Boolean(connections?.google?.customerId);
  const connectionOk = provider === "google" ? googleOk : provider === "meta" ? metaOk : false;

  useEffect(() => {
    setError(null);
    if (campaign?.id) {
      setCampaignId(campaign.id);
      setName(campaign.name || "");
      setProvider(campaign.provider || null);
      setConfig(campaign.config && typeof campaign.config === "object" ? { ...campaign.config } : {});
      setStatus(campaign.status || "draft");
      setExternalId(campaign.external_campaign_id || null);
      return;
    }
    if (campaign?.provider) {
      setProvider(campaign.provider);
      setConfig(campaign.provider === "google" ? defaultGoogleConfig() : defaultMetaConfig());
      return;
    }
    setCampaignId(null);
    setName("");
    setProvider(null);
    setProviderDraft(null);
    setConfig({});
    setStatus("draft");
    setExternalId(null);
  }, [campaign]);

  useEffect(() => {
    if (campaign?.external_campaign_id) setExternalId(campaign.external_campaign_id);
    if (campaign?.status) setStatus(campaign.status);
  }, [campaign?.external_campaign_id, campaign?.status]);

  const loadAnalytics = useCallback(async () => {
    const id = campaignId || campaign?.id;
    if (!id) return;
    setAnalyticsLoading(true);
    setError(null);
    try {
      const res = await fetchData(`/ads/workspace-campaigns/${id}/analytics`, "GET");
      setAnalyticsPayload(res?.data ?? res);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.analyticsLoadError"));
    } finally {
      setAnalyticsLoading(false);
    }
  }, [campaign?.id, campaignId, t]);

  useEffect(() => {
    if (tab === 1 && (campaignId || campaign?.id) && String(status) === "published") {
      loadAnalytics();
    }
  }, [tab, campaignId, campaign?.id, status, loadAnalytics]);

  const pickProvider = (p) => {
    setProvider(p);
    setConfig(p === "google" ? defaultGoogleConfig() : defaultMetaConfig());
  };

  const confirmProviderDraft = () => {
    if (!providerDraft) {
      setError(t("paidAdsPage.pickProviderFirst"));
      return;
    }
    setError(null);
    pickProvider(providerDraft);
  };

  const saveDraft = async () => {
    if (!provider) {
      setError(t("paidAdsPage.pickProviderFirst"));
      return;
    }
    const nm = String(name || "").trim();
    if (!nm) {
      setError(t("paidAdsPage.nameRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const id = campaignId || campaign?.id;
      if (id) {
        await fetchData(`/ads/workspace-campaigns/${id}`, "PUT", { name: nm, config });
      } else {
        const res = await fetchData("/ads/workspace-campaigns", "POST", {
          provider,
          name: nm,
          config,
        });
        const wrap = res?.data;
        const row = wrap?.campaign ?? wrap;
        if (row?.id) {
          setCampaignId(row.id);
          setStatus(row.status);
          setExternalId(row.external_campaign_id || null);
          onCampaignCreated?.(row);
        }
      }
      await onAfterSave?.();
    } catch (e) {
      setError(e?.message || t("paidAdsPage.saveDraftError"));
    } finally {
      setSaving(false);
    }
  };

  const publish = async () => {
    const id = campaignId || campaign?.id;
    if (!id) {
      setError(t("paidAdsPage.saveBeforePublish"));
      return;
    }
    if (!connectionOk) {
      setError(t("paidAdsPage.connectionRequiredPublish"));
      return;
    }
    setPublishing(true);
    setError(null);
    try {
      const res = await fetchData(`/ads/workspace-campaigns/${id}/publish`, "POST", {});
      const wrap = res?.data;
      const jobId = wrap?.jobId ?? res?.jobId;
      if (jobId) {
        const job = await pollJob(jobId);
        const st = job?.status;
        if (st === "failed") {
          setError(job?.error || job?.result?.error || t("paidAdsPage.jobError"));
          setStatus("failed");
        } else {
          setStatus("published");
        }
      }
      await onAfterPublish?.(id);
      await loadAnalytics();
    } catch (e) {
      setError(e?.message || t("paidAdsPage.publishError"));
    } finally {
      setPublishing(false);
    }
  };

  const refreshAnalytics = async () => {
    const id = campaignId || campaign?.id;
    if (!id) return;
    setAnalyticsLoading(true);
    setError(null);
    try {
      const res = await fetchData(`/ads/workspace-campaigns/${id}/analytics/refresh`, "POST", {});
      const d = res?.data ?? res;
      setAnalyticsPayload(d?.analytics ? { analytics: d.analytics } : d);
    } catch (e) {
      setError(e?.message || t("paidAdsPage.analyticsRefreshError"));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const draftSaved = Boolean(campaignId || campaign?.id);
  const publishTooltip = (() => {
    if (!connectionOk) return t("paidAdsPage.publishDisabledNoConnection");
    if (!draftSaved) return t("paidAdsPage.publishDisabledNeedSave");
    return "";
  })();

  const wizardStep = !provider ? 0 : !connectionOk ? 1 : 2;

  const renderConfigField = (field) => {
    const isGoogle = provider === "google";
    const label = isGoogle ? t(`paidAdsPage.googleFields.${field}`) : t(`paidAdsPage.metaFields.${field}`);
    const helpKey = isGoogle ? `paidAdsPage.help_google_${field}` : `paidAdsPage.help_meta_${field}`;
    const help = t(helpKey, { defaultValue: "" });
    return (
      <TextField
        key={field}
        fullWidth
        size="small"
        label={label}
        value={config[field] ?? ""}
        onChange={(e) => setConfig((s) => ({ ...s, [field]: e.target.value }))}
        helperText={help || undefined}
        FormHelperTextProps={{ sx: { mt: 0.5 } }}
      />
    );
  };

  const formTitle = isNew ? t("paidAdsPage.newCampaignTitle") : t("paidAdsPage.editCampaignTitle");

  return (
    <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, width: "100%", maxWidth: 1200, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Typography variant="h6" component="h2" sx={{ pt: 0.25 }}>
          {formTitle}
        </Typography>
        <Button
          variant="outlined"
          size="small"
          color="inherit"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={onClose}
          sx={{ flexShrink: 0 }}
        >
          {t("paidAdsPage.backToList")}
        </Button>
      </Stack>

      {isNew && provider && (
        <Stepper activeStep={wizardStep} sx={{ mb: 3, maxWidth: 720 }}>
          <Step completed={wizardStep > 0}>
            <StepLabel>{t("paidAdsPage.stepChooseNetwork")}</StepLabel>
          </Step>
          <Step completed={wizardStep > 1}>
            <StepLabel>{t("paidAdsPage.stepConnectAccount")}</StepLabel>
          </Step>
          <Step>
            <StepLabel>{t("paidAdsPage.stepCampaignDetails")}</StepLabel>
          </Step>
        </Stepper>
      )}

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity="error"
            sx={{ "& .MuiAlert-message": { width: "100%" } }}
            onClose={() => setError(null)}
          >
            <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
              {error}
            </Typography>
          </Alert>
        </Box>
      )}

      {!provider && isNew && (
        <Stack spacing={2} sx={{ position: "relative" }}>
          <Typography variant="subtitle1">{t("paidAdsPage.selectProviderTitle")}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 900 }}>
            {t("paidAdsPage.selectProviderSubtitle")}
          </Typography>
          <Grid container spacing={2} sx={{ alignItems: "stretch" }}>
            {PAID_ADS_PROVIDER_CARDS.map((c) => (
              <Grid item xs={12} sm={6} key={c.id} sx={{ display: "flex" }}>
                <Box sx={{ display: "flex", width: "100%", minWidth: 0 }}>
                  <PaidAdsProviderTypeCard
                    card={c}
                    selected={providerDraft === c.id}
                    onClick={() => setProviderDraft(c.id)}
                    title={t(`paidAdsPage.providerCard_${c.key}`)}
                    description={t(`paidAdsPage.providerCard_${c.key}Desc`)}
                    selectedLabel={t("paidAdsPage.typeCardSelected")}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="flex-end"
            sx={{
              pt: 2,
              mt: 0,
              flexShrink: 0,
              borderTop: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Button variant="contained" onClick={confirmProviderDraft} disabled={!providerDraft}>
              {t("paidAdsPage.continueWizard")}
            </Button>
          </Stack>
        </Stack>
      )}

      {provider && (
        <>
          {!isNew && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("paidAdsPage.editModeHint")}
            </Alert>
          )}

          {isNew && !draftSaved && (
            <Stack direction="row" sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setProvider(null);
                  setProviderDraft(provider);
                  setError(null);
                }}
              >
                {t("paidAdsPage.backPickProvider")}
              </Button>
            </Stack>
          )}

          {providerSetupProps && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "background.paper" }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                {t("paidAdsPage.inlineAccountsTitle")}
              </Typography>
              {!connectionOk ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {provider === "google" ? t("paidAdsPage.warnGoogleConnection") : t("paidAdsPage.warnMetaConnection")}
                </Alert>
              ) : (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t("paidAdsPage.connectionReadyAlert")}
                </Alert>
              )}
              <PaidAdsProviderSetup provider={provider} {...providerSetupProps} />
              <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {t("paidAdsPage.accountsHintDialog")}
                </Typography>
                <Button size="small" variant="text" onClick={() => onConfigureProvider?.("meta")}>
                  {t("paidAdsPage.configureMetaAccount")}
                </Button>
                <Button size="small" variant="text" onClick={() => onConfigureProvider?.("google")}>
                  {t("paidAdsPage.configureGoogleAccount")}
                </Button>
              </Stack>
            </Paper>
          )}

          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab label={t("paidAdsPage.tabConfig")} />
            <Tab label={t("paidAdsPage.tabAnalytics")} disabled={String(status) !== "published"} />
          </Tabs>

          {tab === 0 && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t("paidAdsPage.sectionCampaignIdentity")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("paidAdsPage.sectionCampaignIdentityHint")}
                </Typography>
                <TextField
                  label={t("paidAdsPage.colCampaignName")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  required
                  size="small"
                />
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {provider === "google" ? t("paidAdsPage.sectionAdContentGoogle") : t("paidAdsPage.sectionAdContentMeta")}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {t("paidAdsPage.bundleFieldsHintShort")}
                </Typography>

                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 1 }}>
                  {provider === "google"
                    ? t("paidAdsPage.sectionAdContentGoogleEssentials")
                    : t("paidAdsPage.sectionAdContentMetaEssential")}
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 2 }}>
                  {(provider === "google" ? GOOGLE_ESSENTIAL : META_ESSENTIAL).map((field) => renderConfigField(field))}
                </Stack>

                {provider === "meta" && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t("paidAdsPage.optionalMetaCreativeHint")}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: optionalMetaCreativeOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                      onClick={() => setOptionalMetaCreativeOpen((v) => !v)}
                      sx={{ mb: optionalMetaCreativeOpen ? 1 : 0 }}
                    >
                      {optionalMetaCreativeOpen
                        ? t("paidAdsPage.toggleMetaCreativeHide")
                        : t("paidAdsPage.toggleMetaCreativeShow")}
                    </Button>
                    <Collapse in={optionalMetaCreativeOpen}>
                      <Stack spacing={1.5}>{META_CREATIVE.map((field) => renderConfigField(field))}</Stack>
                    </Collapse>
                  </Box>
                )}

                {provider === "google" && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t("paidAdsPage.optionalGoogleBiddingHint")}
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      endIcon={
                        <ExpandMoreIcon
                          sx={{
                            transform: optionalGoogleBiddingOpen ? "rotate(180deg)" : "none",
                            transition: "transform 0.2s",
                          }}
                        />
                      }
                      onClick={() => setOptionalGoogleBiddingOpen((v) => !v)}
                      sx={{ mb: optionalGoogleBiddingOpen ? 1 : 0 }}
                    >
                      {optionalGoogleBiddingOpen
                        ? t("paidAdsPage.toggleGoogleBiddingHide")
                        : t("paidAdsPage.toggleGoogleBiddingShow")}
                    </Button>
                    <Collapse in={optionalGoogleBiddingOpen}>
                      <Stack spacing={1.5}>{GOOGLE_BIDDING.map((field) => renderConfigField(field))}</Stack>
                    </Collapse>
                  </Box>
                )}
              </Box>

              <Divider />

              <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
                <Tooltip title={t("paidAdsPage.saveDraftHint")}>
                  <span>
                    <Button variant="contained" onClick={saveDraft} disabled={saving}>
                      {saving ? <CircularProgress size={20} /> : t("paidAdsPage.saveDraft")}
                    </Button>
                  </span>
                </Tooltip>
                {publishTooltip ? (
                  <Tooltip title={publishTooltip}>
                    <span>
                      <Button
                        variant="outlined"
                        onClick={publish}
                        disabled={publishing || saving || !connectionOk || !draftSaved}
                      >
                        {publishing ? <CircularProgress size={20} /> : t("paidAdsPage.publish")}
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={publish}
                    disabled={publishing || saving || !connectionOk || !draftSaved}
                  >
                    {publishing ? <CircularProgress size={20} /> : t("paidAdsPage.publish")}
                  </Button>
                )}
              </Stack>
              {(campaignId || campaign?.id) && (
                <Typography variant="caption" color="text.secondary">
                  {t("paidAdsPage.statusLine", {
                    status: t(`paidAdsPage.status_${status}`, { defaultValue: status }),
                    ext: externalId || "—",
                  })}
                </Typography>
              )}
            </Stack>
          )}

          {tab === 1 && (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Button size="small" variant="outlined" onClick={refreshAnalytics} disabled={analyticsLoading}>
                  {analyticsLoading ? <CircularProgress size={18} /> : t("paidAdsPage.refreshAnalytics")}
                </Button>
              </Stack>
              {!analyticsPayload?.analytics && !analyticsLoading && (
                <Alert severity="info" variant="outlined">
                  {t("paidAdsPage.analyticsEmptyGuided")}
                </Alert>
              )}
              {(analyticsPayload?.analytics || analyticsPayload?.metrics) && (
                <Box
                  component="pre"
                  sx={{ p: 2, bgcolor: "action.hover", borderRadius: 1, fontSize: 12, overflow: "auto" }}
                >
                  {JSON.stringify(analyticsPayload?.analytics || analyticsPayload, null, 2)}
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
